"use client";

import { useLang } from "@/contexts/language-context";
import { motion } from "framer-motion";
import { ArrowUpRight, MessageSquare, MonitorPlay, Tablet, WifiOff } from "lucide-react";
import Link from "next/link";

const PAIRS = [
  {
    icon: WifiOff,
    slug: "works-offline",
    title: { en: "Works offline", es: "Funciona offline" },
    body: {
      en: "Festivals, parking lots, dead-zone events. Keeps ringing cash, syncs when WiFi comes back.",
      es: "Festivales, lotes, eventos sin señal. Sigue cobrando en efectivo, sincroniza al volver el WiFi.",
    },
  },
  {
    icon: Tablet,
    slug: "self-order-kiosk",
    title: { en: "Self-order kiosk", es: "Kiosko de auto-orden" },
    body: {
      en: "Spare iPad becomes a customer-facing kiosk. Bigger orders, fewer asks.",
      es: "Un iPad extra se vuelve un kiosko para el cliente. Órdenes más grandes, menos preguntas.",
    },
  },
  {
    icon: MessageSquare,
    slug: "sms-customers-staff",
    title: { en: "SMS to customers", es: "SMS a clientes" },
    body: {
      en: "Auto-text when their order's ready. They walk back without waiting at the window.",
      es: "Texto automático cuando está listo. Regresan sin esperar en la ventana.",
    },
  },
  {
    icon: MonitorPlay,
    slug: "live-menu-boards",
    title: { en: "Live menu boards", es: "Menus en vivo" },
    body: {
      en: "Edit a price on the iPad — the TV menu updates in seconds. No reprinting laminates.",
      es: "Cambia un precio en el iPad — el TV se actualiza en segundos. Sin reimprimir laminados.",
    },
  },
];

export default function FoodTruckQsrPairs() {
  const { t, lang } = useLang();

  return (
    <section className="py-24 px-6 bg-redy-warm-white">
      <div className="max-w-[1100px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-redy-orange mb-4">
            {t("Pairs well with", "Combina con")}
          </div>
          <h2 className="text-[36px] md:text-[52px] font-black leading-[1.04] tracking-tight text-redy-charcoal max-w-[720px] mx-auto">
            {t("The features food trucks pull hardest.", "Las features que más usan los food trucks.")}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {PAIRS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.4, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  href={`/features/${p.slug}`}
                  className="group block bg-white border border-black/[0.04] rounded-3xl p-7 hover:shadow-[0_20px_40px_-20px_rgba(0,0,0,0.12)] transition-all hover:-translate-y-0.5"
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-11 h-11 bg-redy-charcoal rounded-xl flex items-center justify-center group-hover:bg-redy-orange transition-colors">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-redy-charcoal/30 group-hover:text-redy-orange group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                  <h3 className="text-lg font-black text-redy-charcoal mb-2">
                    {p.title[lang as "en" | "es"] ?? p.title.en}
                  </h3>
                  <p className="text-sm text-redy-secondary leading-relaxed">
                    {p.body[lang as "en" | "es"] ?? p.body.en}
                  </p>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
